import * as Yup from 'yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Stack, Alert, IconButton, InputAdornment, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField } from '../../../components/hook-form';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { login } = useAuth();
  const isMountedRef = useIsMountedRef();
  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    username: Yup.string().required('Username is required'),
    password: Yup.string().min(6).required('Password is required'),
  });

  const defaultValues = {
    username: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      await login(data.username, data.password);
    } catch (error) {
      reset();
      if (isMountedRef.current) {
        setError('afterSubmit', { message: error.message });
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ width: '100%' }}>
        <Stack spacing={{ xs: 3, sm: 4 }}>
          {!!errors.afterSubmit && (
            <Alert
              severity="error"
              sx={{
                borderRadius: 2,
                animation: 'fadeIn 0.5s ease-in-out',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0, transform: 'translateY(-10px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
                '& .MuiAlert-message': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                },
              }}
            >
              {errors.afterSubmit.message}
            </Alert>
          )}

          <RHFTextField
            name="username"
            label="USERNAME"
            inputProps={{
              style: {
                textTransform: 'lowercase', // Changed from uppercase to lowercase
              }
            }}
            onChange={(e) => {
              e.target.value = e.target.value.toLowerCase(); // Force lowercase
              methods.setValue('username', e.target.value);
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem',
                backgroundColor: '#f8f9fa',
                color: "#000",
                height: 56,
                '& input': {
                  padding: '8px 14px',
                  textTransform: 'lowercase', // Changed from uppercase to lowercase
                },
                '&:hover': {
                  backgroundColor: '#fff',
                  boxShadow: '0 0 0 1px rgba(46, 125, 50, 0.25)',
                },
                '&.Mui-focused': {
                  backgroundColor: '#fff',
                  boxShadow: '0 0 0 2px rgba(46, 125, 50, 0.3)',
                },
                '&.Mui-error': {
                  backgroundColor: '#fff',
                  boxShadow: '0 0 0 2px rgba(211, 47, 47, 0.3)',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#637381',
                fontSize: '0.875rem',
                fontWeight: 500,
                backgroundColor: '#f8f9fa',
                padding: '0 8px',
                transform: 'translate(6px, -12px) scale(0.75)',
                '&.Mui-focused': {
                  color: '#2E7D32',
                  backgroundColor: '#fff',
                },
                '&.Mui-error': {
                  color: '#d32f2f',
                  backgroundColor: '#fff',
                },
                '&.MuiFormLabel-filled': {
                  backgroundColor: '#f8f9fa',
                },
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
                marginLeft: '14px',
              },
              '& .MuiFormHelperText-root.Mui-error': {
                color: '#d32f2f',
                fontWeight: 500,
              },
            }}
          />

          <RHFTextField
            name="password"
            label="PASSWORD"
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{
                      color: '#637381',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <Iconify
                      icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                      sx={{ fontSize: 20 }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem',
                backgroundColor: '#f8f9fa',
                height: 56,
                color: "#000",
                '& input': {
                  padding: '8px 14px',
                },
                '&:hover': {
                  backgroundColor: '#fff',
                  boxShadow: '0 0 0 1px rgba(46, 125, 50, 0.25)',
                },
                '&.Mui-focused': {
                  backgroundColor: '#fff',
                  boxShadow: '0 0 0 2px rgba(46, 125, 50, 0.3)',
                },
                '&.Mui-error': {
                  backgroundColor: '#fff',
                  boxShadow: '0 0 0 2px rgba(211, 47, 47, 0.3)',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#637381',
                fontSize: '0.875rem',
                fontWeight: 500,
                backgroundColor: '#f8f9fa',
                padding: '0 8px',
                transform: 'translate(6px, -12px) scale(0.75)',
                '&.Mui-focused': {
                  color: '#2E7D32',
                  backgroundColor: '#fff',
                },
                '&.Mui-error': {
                  color: '#d32f2f',
                  backgroundColor: '#fff',
                },
                '&.MuiFormLabel-filled': {
                  backgroundColor: '#f8f9fa',
                },
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
                marginLeft: '14px',
              },
              '& .MuiFormHelperText-root.Mui-error': {
                color: '#d32f2f',
                fontWeight: 500,
              },
            }}
          />
        </Stack>

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          sx={{
            mt: { xs: 4, md: 4 },
            borderRadius: 2,
            height: { xs: '48px', md: '50px' },
            fontSize: { xs: '1rem', md: '1rem' },
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)', // Green gradient
            boxShadow: '0 8px 24px 0 rgba(46, 125, 50, 0.25)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 32px 0 rgba(46, 125, 50, 0.35)',
              background: 'linear-gradient(135deg, #33691E 0%, #1B5E20 100%)', // Darker green on hover
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: '0 8px 24px 0 rgba(46, 125, 50, 0.25)',
            },
            '&.Mui-disabled': {
              opacity: 0.7,
            },
          }}
        >
          SIGN IN
        </LoadingButton>
      </Box>
    </FormProvider>
  );
}
